import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import { isNative } from '@unifyapps/ui/utils/isNative';
import { produce } from 'immer';
import _isString from 'lodash/isString';
import _set from 'lodash/set';
import invariant from 'ts-invariant';
import type { StoreApi } from 'zustand/vanilla';
import { safeJsonParse, safeJsonStringify } from '../../../../../utils/json';
import { sessionStorage } from '../../../../../utils/storage';
import { INTERNALS_PARAMETERS_KEY } from '../../../../const';
import InterfaceHelper from '../../../InterfaceStore/helper/InterfaceHelper';
import type { GlobalAppStateStore, SessionStorageActionProps } from '../types';
import { validateStorageKey, validateStorageValue } from '../utils/validation';

const updateStorageAndState = (
  get: StoreApi<GlobalAppStateStore>['getState'],
  set: StoreApi<GlobalAppStateStore>['setState'],
  storageKey: string,
  path: string | string[],
  value: unknown,
) => {
  try {
    const existingData = sessionStorage.getItem(storageKey);
    const parsedData = existingData
      ? safeJsonParse<{ values?: Record<string, unknown> }>(existingData, { values: {} })
      : { values: {} };

    if (!parsedData.values) {
      parsedData.values = {};
    }

    _set(parsedData.values, path, value);

    const stringifiedData = safeJsonStringify(parsedData);
    if (stringifiedData === null) {
      throw new Error('Failed to stringify sessionStorage data');
    }
    sessionStorage.setItem(storageKey, stringifiedData);

    const updatedSessionStorage = produce(get().sessionStorage, (draft) => {
      if (!draft.values) {
        draft.values = {};
      }
      _set(draft.values, path, value);
    });

    set((state) => ({
      ...state,
      sessionStorage: updatedSessionStorage,
    }));
  } catch (error) {
    console.error('Failed to update sessionStorage:', error);
    throw new Error('Failed to update sessionStorage');
  }
};

/**
 * Action creator for managing sessionStorage operations
 */
const getSessionStorageAction =
  (
    get: StoreApi<GlobalAppStateStore>['getState'],
    set: StoreApi<GlobalAppStateStore>['setState'],
  ) =>
  ({ operation, payload }: SessionStorageActionProps) => {
    const storageKey = InterfaceHelper.getSessionStorageKey(get().interfaceRecord.id);

    try {
      switch (operation) {
        case 'SET_IN': {
          const { path, value } = payload;
          if (process.env.NODE_ENV === 'development' && !isNative()) {
            invariant(path, 'path is required in SET_IN');
          }
          if (!path) break;
          validateStorageKey(path, 'SET_IN');
          validateStorageValue(value);
          updateStorageAndState(get, set, storageKey, path, value);
          break;
        }
        case 'REMOVE': {
          const { path } = payload;
          if (process.env.NODE_ENV === 'development' && !isNative()) {
            invariant(path, 'path is required in REMOVE');
          }
          if (!path) break;
          validateStorageKey(path, 'REMOVE');
          updateStorageAndState(get, set, storageKey, path, undefined);
          break;
        }
        case 'CLEAR': {
          sessionStorage.removeItem(storageKey);
          set((state) => ({
            ...state,
            sessionStorage: { values: {} },
          }));
          break;
        }
        case 'SET_IN_BLOCK_STATE': {
          const { path: propertyPath, value, interfacePageId } = payload;

          const updatedSessionStorage = produce(get().sessionStorage, (draft) => {
            if (!draft.values) {
              draft.values = {};
            }
            const path = _isString(propertyPath) ? [propertyPath] : propertyPath;
            // setting the blocks internal state inside __internals__ so we can omit this whole before sending to server for generating schema
            _set(draft.values, [INTERNALS_PARAMETERS_KEY, interfacePageId, ...(path ?? [])], value);
          });

          try {
            const existingData = sessionStorage.getItem(storageKey);
            const parsedData = existingData
              ? safeJsonParse<{ values?: Record<string, unknown> }>(existingData, { values: {} })
              : { values: {} };

            if (!parsedData.values) {
              parsedData.values = {};
            }

            const path = _isString(propertyPath) ? [propertyPath] : propertyPath;
            _set(
              parsedData.values,
              [INTERNALS_PARAMETERS_KEY, interfacePageId, ...(path ?? [])],
              value,
            );

            const stringifiedData = safeJsonStringify(parsedData);
            if (stringifiedData === null) {
              throw new Error('Failed to stringify sessionStorage data');
            }
            sessionStorage.setItem(storageKey, stringifiedData);

            set((state) => ({
              ...state,
              sessionStorage: updatedSessionStorage,
            }));
          } catch (error) {
            console.error('Failed to update sessionStorage:', error);
            throw new Error('Failed to update sessionStorage');
          }
          break;
        }
        default:
          throw new Error(`Unknown sessionStorage operation: ${operation}`);
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('SessionStorage operation failed:', errorMessage);
      throw error;
    }
  };

export default getSessionStorageAction;

export const getInitializeSessionStorage = (
  interfaceRecord: InterfaceEntity,
): { values: Record<string, unknown> } => {
  try {
    const storageKey = InterfaceHelper.getSessionStorageKey(interfaceRecord.id);
    const value = sessionStorage.getItem(storageKey);
    if (value !== null) {
      try {
        const parsedData = safeJsonParse<{ values: Record<string, unknown> }>(value);
        return parsedData;
      } catch (error) {
        console.error('Failed to parse sessionStorage data, resetting to empty state', error);
        return { values: {} };
      }
    }
  } catch (error) {
    console.warn('Failed to initialize sessionStorage:', error);
  }
  return { values: {} };
};
